import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Row,
  Col,
  message,
  Card,
  Typography,
  Divider,
  Space,
  Tooltip,
  Alert,
  Upload,
} from "antd";
import {
  UserOutlined,
  FileTextOutlined,
  CalendarOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import type {
  HRContract,
  HREmployee,
  HRContractType,
  ContractFormData,
} from "../../types/hr";
import { useHRAPI } from "../../hooks/useHRAPI";
import dayjs from "dayjs";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface ContractFormProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  contract?: HRContract | null;
  employees: HREmployee[];
  contractTypes: HRContractType[];
}

const ContractForm: React.FC<ContractFormProps> = ({
  visible,
  onCancel,
  onSuccess,
  contract,
  employees,
  contractTypes,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<HREmployee | null>(
    null
  );
  const [selectedContractType, setSelectedContractType] =
    useState<HRContractType | null>(null);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const { createContract, updateContract, getCurrencies } = useHRAPI();

  const isEditing = !!contract;

  // Charger les devises au début
  useEffect(() => {
    const fetchCurrencies = async () => {
      setLoadingCurrencies(true);
      try {
        const currenciesList = await getCurrencies();
        setCurrencies(currenciesList || []);
      } catch (error) {
        console.error("Erreur lors du chargement des devises:", error);
        message.error("Erreur lors du chargement des devises");
      } finally {
        setLoadingCurrencies(false);
      }
    };

    if (visible) {
      fetchCurrencies();
    }
  }, [visible, getCurrencies]);

  useEffect(() => {
    if (visible) {
      if (contract) {
        // Mode édition - pré-remplir le formulaire
        const employee = employees.find((e) => e.id === contract.employee_id);
        const contractType = contractTypes.find(
          (ct) => ct.id === contract.contract_type_id
        );

        setSelectedEmployee(employee || null);
        setSelectedContractType(contractType || null);

        form.setFieldsValue({
          employee_id: contract.employee_id,
          contract_type_id: contract.contract_type_id,
          contract_number: contract.contract_number,
          start_date: contract.start_date ? dayjs(contract.start_date) : null,
          end_date: contract.end_date ? dayjs(contract.end_date) : null,
          trial_period_end_date: contract.trial_period_end_date
            ? dayjs(contract.trial_period_end_date)
            : null,
          base_salary: contract.base_salary,
          gross_salary: contract.gross_salary,
          currency_id: contract.currency_id || 1,
          salary_type: contract.salary_type,
          working_hours_per_week: contract.working_hours_per_week,
          status: contract.status,
          termination_reason: contract.termination_reason,
          notes: contract.notes,
        });
      } else {
        // Mode création - réinitialiser le formulaire
        form.resetFields();
        setSelectedEmployee(null);
        setSelectedContractType(null);

        // Valeurs par défaut
        form.setFieldsValue({
          currency_id: 1, // FCFA par défaut
          salary_type: "monthly",
          working_hours_per_week: 40,
          status: "draft",
        });
      }
    }
  }, [visible, contract, form, employees, contractTypes]);

  const handleEmployeeChange = (employeeId: number) => {
    const employee = employees.find((e) => e.id === employeeId);
    setSelectedEmployee(employee || null);
  };

  const handleContractTypeChange = (contractTypeId: number) => {
    const contractType = contractTypes.find((ct) => ct.id === contractTypeId);
    setSelectedContractType(contractType || null);

    // Auto-calculer la date de fin d'essai si le type de contrat a une période d'essai
    if (contractType && contractType.trial_period_days > 0) {
      const startDate = form.getFieldValue("start_date");
      if (startDate) {
        const trialEndDate = startDate.add(
          contractType.trial_period_days,
          "day"
        );
        form.setFieldValue("trial_period_end_date", trialEndDate);
      }
    }

    // Auto-calculer la date de fin si le type de contrat a une durée maximale
    if (contractType && contractType.max_duration_months) {
      const startDate = form.getFieldValue("start_date");
      if (startDate) {
        const endDate = startDate.add(
          contractType.max_duration_months,
          "month"
        );
        form.setFieldValue("end_date", endDate);
      }
    }
  };

  const handleStartDateChange = (date: dayjs.Dayjs | null) => {
    if (date && selectedContractType) {
      // Recalculer la date de fin d'essai
      if (selectedContractType.trial_period_days > 0) {
        const trialEndDate = date.add(
          selectedContractType.trial_period_days,
          "day"
        );
        form.setFieldValue("trial_period_end_date", trialEndDate);
      }

      // Recalculer la date de fin si durée maximale définie
      if (selectedContractType.max_duration_months) {
        const endDate = date.add(
          selectedContractType.max_duration_months,
          "month"
        );
        form.setFieldValue("end_date", endDate);
      }
    }
  };

  const formatCurrency = (amount: number) => {
    const selectedCurrencyId = form.getFieldValue("currency_id") || 1;
    const selectedCurrency = currencies.find(
      (c) => c.id === selectedCurrencyId
    );
    const currencyCode = selectedCurrency?.code || "XOF";
    const currencySymbol = selectedCurrency?.symbol || "FCFA";

    return `${new Intl.NumberFormat("fr-FR").format(amount)} ${currencySymbol}`;
  };

  const calculateTotalSalary = () => {
    const grossSalary = form.getFieldValue("gross_salary") || 0;
    return grossSalary;
  };

  const handleSubmit = async (values: ContractFormData) => {
    setLoading(true);
    try {
      const formattedValues: any = {
        ...values,
        start_date: values.start_date
          ? dayjs(values.start_date).format("YYYY-MM-DD")
          : "",
        end_date: values.end_date
          ? dayjs(values.end_date).format("YYYY-MM-DD")
          : undefined,
        trial_period_end_date: values.trial_period_end_date
          ? dayjs(values.trial_period_end_date).format("YYYY-MM-DD")
          : undefined,
        base_salary: Number(values.base_salary),
        gross_salary: Number(values.gross_salary),
        currency_id: Number(values.currency_id || 1),
        working_hours_per_week: Number(values.working_hours_per_week),
      };

      // Traitement des fichiers uploadés
      if (
        Array.isArray(values.contract_file) &&
        values.contract_file.length > 0
      ) {
        const file = values.contract_file[0] as any;
        if (file.originFileObj) {
          formattedValues.contract_file = file.originFileObj;
        }
      }

      if (
        Array.isArray(values.signed_contract_file) &&
        values.signed_contract_file.length > 0
      ) {
        const file = values.signed_contract_file[0] as any;
        if (file.originFileObj) {
          formattedValues.signed_contract_file = file.originFileObj;
        }
      }

      let result;
      if (isEditing) {
        result = await updateContract(contract!.id, formattedValues);
      } else {
        result = await createContract(formattedValues);
      }

      if (result !== null) {
        message.success(
          isEditing ? "Contrat modifié avec succès" : "Contrat créé avec succès"
        );
        form.resetFields();
        onSuccess();
      } else {
        throw new Error("Erreur lors de la sauvegarde");
      }
    } catch (error: any) {
      console.error("Erreur lors de la sauvegarde:", error);
      message.error(error.message || "Erreur lors de la sauvegarde du contrat");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedEmployee(null);
    setSelectedContractType(null);
    onCancel();
  };

  const activeEmployees = employees.filter((emp) => emp.status === "active");

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FileTextOutlined />
          {isEditing ? "Modifier le contrat" : "Nouveau contrat"}
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={900}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          currency_id: 1,
          salary_type: "monthly",
          working_hours_per_week: 40,
          status: "draft",
        }}
      >
        {/* Informations générales */}
        <Card
          title="Informations générales"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Employé"
                name="employee_id"
                rules={[
                  {
                    required: true,
                    message: "Veuillez sélectionner un employé",
                  },
                ]}
              >
                <Select
                  placeholder="Sélectionner un employé"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleEmployeeChange}
                  suffixIcon={<UserOutlined />}
                >
                  {activeEmployees.map((employee) => (
                    <Option key={employee.id} value={employee.id}>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {employee.first_name} {employee.last_name}
                        </div>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {employee.employee_number} - {employee.position_title}
                        </Text>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Type de contrat"
                name="contract_type_id"
                rules={[
                  {
                    required: true,
                    message: "Veuillez sélectionner un type de contrat",
                  },
                ]}
              >
                <Select
                  placeholder="Sélectionner le type de contrat"
                  onChange={handleContractTypeChange}
                >
                  {contractTypes
                    .filter((ct) => ct.is_active)
                    .map((type) => (
                      <Option key={type.id} value={type.id}>
                        <div>
                          <div style={{ fontWeight: 500 }}>{type.name}</div>
                          {type.description && (
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              {type.description}
                            </Text>
                          )}
                        </div>
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Numéro de contrat" name="contract_number">
                <Input placeholder="Ex: CNT-2024-001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Statut"
                name="status"
                rules={[
                  {
                    required: true,
                    message: "Veuillez sélectionner un statut",
                  },
                ]}
              >
                <Select>
                  <Option value="draft">Brouillon</Option>
                  <Option value="active">Actif</Option>
                  <Option value="suspended">Suspendu</Option>
                  <Option value="terminated">Terminé</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Informations temporelles */}
        <Card
          title="Période du contrat"
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Date de début"
                name="start_date"
                rules={[
                  {
                    required: true,
                    message: "Veuillez sélectionner la date de début",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Date de début"
                  onChange={handleStartDateChange}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Date de fin" name="end_date">
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Date de fin (optionnelle)"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={
                  <span>
                    Fin période d'essai{" "}
                    <Tooltip title="Calculée automatiquement selon le type de contrat">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </span>
                }
                name="trial_period_end_date"
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder="Fin d'essai"
                />
              </Form.Item>
            </Col>
          </Row>

          {selectedContractType && (
            <Alert
              message={
                <div>
                  <strong>Informations du type de contrat :</strong>
                  <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
                    {selectedContractType.max_duration_months && (
                      <li>
                        Durée maximale :{" "}
                        {selectedContractType.max_duration_months} mois
                      </li>
                    )}
                    {selectedContractType.trial_period_days > 0 && (
                      <li>
                        Période d'essai :{" "}
                        {selectedContractType.trial_period_days} jours
                      </li>
                    )}
                    <li>
                      Renouvelable :{" "}
                      {selectedContractType.renewable ? "Oui" : "Non"}
                    </li>
                    <li>
                      Préavis : {selectedContractType.notice_period_days} jours
                    </li>
                  </ul>
                </div>
              }
              type="info"
              showIcon
              style={{ marginTop: 8 }}
            />
          )}
        </Card>

        {/* Informations salariales */}
        <Card
          title={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Rémunération</span>
              <Text strong style={{ color: "#1890ff" }}>
                Salaire brut : {formatCurrency(calculateTotalSalary())}
              </Text>
            </div>
          }
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Salaire de base"
                name="base_salary"
                rules={[
                  {
                    required: true,
                    message: "Veuillez saisir le salaire de base",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="Salaire de base"
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Salaire brut"
                name="gross_salary"
                rules={[
                  {
                    required: true,
                    message: "Veuillez saisir le salaire brut",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="Salaire brut"
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Devise"
                name="currency_id"
                rules={[
                  {
                    required: true,
                    message: "Veuillez sélectionner une devise",
                  },
                ]}
              >
                <Select
                  loading={loadingCurrencies}
                  placeholder="Sélectionner une devise"
                  onChange={() =>
                    form.validateFields(["base_salary", "gross_salary"])
                  } // Force re-render du formatage
                >
                  {currencies.map((currency) => (
                    <Option key={currency.id} value={currency.id}>
                      {currency.name} ({currency.symbol})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Type de salaire"
                name="salary_type"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="monthly">Mensuel</Option>
                  <Option value="daily">Journalier</Option>
                  <Option value="hourly">Horaire</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Heures de travail par semaine"
                name="working_hours_per_week"
                rules={[
                  {
                    required: true,
                    message: "Veuillez saisir les heures de travail",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={1}
                  max={60}
                  placeholder="40"
                  addonAfter="heures"
                />
              </Form.Item>
            </Col>
          </Row>

          <Alert
            message="Information importante"
            description="Les éléments de rémunération détaillés (indemnités, primes, etc.) sont gérés dans le menu 'Paie & Rémunération' > 'Éléments de paie' pour chaque employé."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Card>

        {/* Informations complémentaires */}
        <Card
          title="Informations complémentaires"
          size="small"
          style={{ marginBottom: 16 }}
        >
          {form.getFieldValue("status") === "terminated" && (
            <Form.Item
              label="Motif de fin de contrat"
              name="termination_reason"
            >
              <TextArea
                rows={3}
                placeholder="Précisez le motif de fin de contrat..."
              />
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Fichier du contrat"
                name="contract_file"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e?.fileList;
                }}
              >
                <Upload
                  name="contract_file"
                  accept=".pdf,.doc,.docx"
                  maxCount={1}
                  beforeUpload={() => false} // Prevent auto upload
                >
                  <Button icon={<UploadOutlined />}>
                    Sélectionner le fichier du contrat
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Contrat signé"
                name="signed_contract_file"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e?.fileList;
                }}
              >
                <Upload
                  name="signed_contract_file"
                  accept=".pdf,.doc,.docx"
                  maxCount={1}
                  beforeUpload={() => false} // Prevent auto upload
                >
                  <Button icon={<UploadOutlined />}>
                    Sélectionner le contrat signé
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Notes" name="notes">
            <TextArea
              rows={4}
              placeholder="Notes complémentaires sur le contrat..."
            />
          </Form.Item>
        </Card>

        {/* Boutons d'action */}
        <div
          style={{
            textAlign: "right",
            borderTop: "1px solid #f0f0f0",
            paddingTop: 16,
          }}
        >
          <Space>
            <Button onClick={handleCancel}>Annuler</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEditing ? "Modifier" : "Créer"} le contrat
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default ContractForm;
